import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { Button } from "react-bootstrap";
import { CategoryPickerComponent } from "@me-pos/category-picker";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { useReactiveVar } from "@apollo/client";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { Spinner } from "@services/ui-components";

import { EditMenuService } from "../service/editMenuService";
import { useGetAllMenuCategoriesForEditPageQuery } from "@me-team/host/main/editMenuPage/graphql/editMenuPage.hooks";
import { SelectedMenuTarget } from "../MenuByCategoryComponent/MenuByCategoryComponent";
import { menuLanguageVersionVar } from "@me-team/host/main/menuLanguageVersions/state";
import { useMenuLanguagesData } from "../../Me-menu/utils/hooks/useMenuLanguagesData";
import { buildTranslationTextVm } from "../../Me-menu/utils/translationVersions/buildTranslationTextVm";
import TranslationMissingDot from "../../Me-menu/components/LanguageVersionSelect/TranslationMissingDot";
import { CompanyAccess } from "@me-team/host/main/graphql/types";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";
import { cloneNameToDisplay } from "../../Me-menu/utils/cloneNameUtils";

type MoveMenuItemModalProps = {
    isOpen: boolean;
    menuId: number;
    menuItemTarget: SelectedMenuTarget | null;
    onCloseMoveModal: () => void;
    moveCategoryHandler: (categoryId: number, subcategoryId: number | null) => Promise<void> | void;
    canEditMenuContent?: boolean;
};

type SelectedCategoryState = {
    currentCategoryId: number | null;
    currentSubcategoryId: number | null;
};

const MoveMenuItemModal: React.FC<MoveMenuItemModalProps> = (props) => {
    const { t: translate } = useTranslation();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const selectedLangVersionCode = useReactiveVar(menuLanguageVersionVar);
    const { primaryLanguage } = useMenuLanguagesData(Number(currentCompanyId));
    const primaryLanguageIso = primaryLanguage?.isoCode?.trim() || null;
    const isTranslationMode = Boolean(
        selectedLangVersionCode && primaryLanguageIso && selectedLangVersionCode !== primaryLanguageIso
    );
    const translationTooltip = translate("Edit the language version");
    const canEditMenuContentByRole = useRoleAccess(CompanyAccess.MenuCloneEdit);
    const canEditMenuContent = props.canEditMenuContent ?? canEditMenuContentByRole;

    const dishNameVm = buildTranslationTextVm({
        translatedValue: props.menuItemTarget?.dishName,
        originValue: props.menuItemTarget?.dishNameOrigin,
        isTranslationMode,
    });

    const [selectedCategory, setSelectedCategory] = useState<SelectedCategoryState>({
        currentCategoryId: null,
        currentSubcategoryId: null,
    });

    const { data, loading, error } = useGetAllMenuCategoriesForEditPageQuery({
        skip: !props.isOpen || !currentCompanyId || !selectedLangVersionCode,
        variables: {
            companyId: Number(currentCompanyId),
            locale: selectedLangVersionCode,
            menuId: props.menuId,
            keepWithoutMenuRelation: true,
        },
        fetchPolicy: "network-only",
    });

    useEffect(() => {
        if (!props.isOpen) {
            setSelectedCategory({ currentCategoryId: null, currentSubcategoryId: null });
        }
    }, [props.isOpen]);

    const serviceCategories = useMemo(() => {
        const categories = data?.user?.company?.meMenuCategories;
        if (!categories) return null;

        return EditMenuService.categoryModuleCreator(
            categories,
            selectedCategory.currentCategoryId,
            selectedCategory.currentSubcategoryId,
            isTranslationMode
        );
    }, [data, isTranslationMode, selectedCategory.currentCategoryId, selectedCategory.currentSubcategoryId]);

    const canMove =
        Boolean(props.menuItemTarget) &&
        Boolean(selectedCategory.currentCategoryId) &&
        !loading &&
        !error &&
        canEditMenuContent;

    const dishNameLabel = dishNameVm.displayText ? `"${cloneNameToDisplay(dishNameVm.displayText, translate)}"` : "";
    const moveDishTitle = `${translate("Move dish")} ${dishNameLabel}`;

    const handleMove = async () => {
        if (!canEditMenuContent) return;
        if (!props.menuItemTarget) return;
        if (!selectedCategory.currentCategoryId) return;

        await props.moveCategoryHandler(selectedCategory.currentCategoryId, selectedCategory.currentSubcategoryId);

        props.onCloseMoveModal();
    };

    return (
        <Modal
            show={props.isOpen}
            onHide={props.onCloseMoveModal}
            centered
            animation
            aria-labelledby="move-menu-item-modal"
        >
            <Modal.Header closeButton className="border-bottom-0">
                <Modal.Title id="move-menu-item-modal">
                    <h5 className="mb-0">
                        <span className="me-1">{moveDishTitle}</span>
                        {dishNameVm.isMissingTranslation ? (
                            <TranslationMissingDot tooltipText={translationTooltip} />
                        ) : null}
                    </h5>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {loading && (
                    <div className="d-flex justify-content-center py-4">
                        <Spinner heightContent={true} />
                    </div>
                )}

                {!loading && error && <div className="text-danger">{translate("Something went wrong")}</div>}

                {!loading && !error && serviceCategories && (
                    <CategoryPickerComponent
                        i18n={i18next}
                        serviceCategories={serviceCategories}
                        showMissingTranslationDot={isTranslationMode}
                        renderMissingTranslationDot={() => <TranslationMissingDot tooltipText={translationTooltip} />}
                        onSearchChange={() => {}}
                        onChangeCategory={(currentCategory) => {
                            if (currentCategory.isPicked) {
                                setSelectedCategory({
                                    currentCategoryId: currentCategory.id,
                                    currentSubcategoryId: null,
                                });
                            } else {
                                setSelectedCategory({
                                    currentCategoryId: null,
                                    currentSubcategoryId: null,
                                });
                            }
                        }}
                        onChangeSubcategory={(pickedSubcategory, currentCategory) => {
                            if (pickedSubcategory.isPicked) {
                                setSelectedCategory({
                                    currentCategoryId: currentCategory.id,
                                    currentSubcategoryId: pickedSubcategory.id,
                                });
                            } else {
                                setSelectedCategory({
                                    currentCategoryId: null,
                                    currentSubcategoryId: null,
                                });
                            }
                        }}
                        isOpen
                    />
                )}
            </Modal.Body>

            <Modal.Footer className="border-top-0">
                <Button variant="primary" className="fw-normal col-4" disabled={!canMove} onClick={handleMove}>
                    {translate("servicesPage.moveModal.moveBtn")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default React.memo(MoveMenuItemModal);
